import './index.css';
import 'react-pdf/dist/Page/TextLayer.css';

import * as Sentry from '@sentry/react';
import { getSentryEnvironment } from '@warden/warden-common/src/shared/sentry';
import { enableMapSet } from 'immer';
import React from 'react';
import ReactDOM from 'react-dom/client';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import Loader from './components/primitives/Loader';
import { getSentryConfig } from './config/sentry';
import { shouldDropSentryEvent } from './config/sentryFilters';
import reportWebVitals from './reportWebVitals';
import { isDevelopment, isProd, isStaging } from './utils/utils';

enableMapSet();

const enableDevEnvSentry = import.meta.env.VITE_DEV_ENV_SENTRY === 'true';
if (!isDevelopment() || enableDevEnvSentry) {
  const config = getSentryConfig();
  Sentry.init({
    dsn: 'https://ad67063c1c956f1724d232f99bd86d1f@o4505950121164800.ingest.sentry.io/4505950124900352',
    environment: getSentryEnvironment({
      isProd: isProd(),
      isStaging: isStaging(),
      isDev: isDevelopment(),
    }),
    integrations: [
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),
      Sentry.replayIntegration(),
      Sentry.captureConsoleIntegration({ levels: ['error'] }),

      /* Temporarily disabled
      Sentry.feedbackIntegration({
        colorScheme: 'system',
        autoInject: false,
      }),
      */
    ],
    beforeSend: (event) => (shouldDropSentryEvent(event) ? null : event),
    sendDefaultPii: config.sendDefaultPii,
    maxValueLength: config.maxValueLength,
    tracesSampleRate: config.tracesSampleRate,
    tracePropagationTargets: config.tracePropagationTargets,
    replaysSessionSampleRate: config.replaysSessionSampleRate,
    replaysOnErrorSampleRate: config.replaysOnErrorSampleRate,
  });
}

const App = React.lazy(() => import('./App'));

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

if (import.meta.env.VITE_IS_IN_E2E === 'true') {
  root.render(
    <React.Suspense fallback={<Loader />}>
      <App />
    </React.Suspense>
  );
} else {
  root.render(
    <React.StrictMode>
      <React.Suspense fallback={<Loader />}>
        <App />
      </React.Suspense>
    </React.StrictMode>
  );
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
